import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, collection, addDoc, getDocs } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { useNotification } from '../../universal/context/NotificationContext';
import { dbModernCasinos } from '../../App';
import { Link } from 'react-router-dom';

/////////////////////////////////////////////
// Interfaces
/////////////////////////////////////////////

interface ICasino {
  hasRibbon: boolean;
  ribbonText: string;
  ribbonType: string; // either 'new' or 'default'
  position: number;
  image: string; // original parsed image URL
  bonus: string;
  termsPlain: string;
  termsLinks: string[];
  term1?: string;
  term2?: string;
  term3?: string;
  gotoLink: string;
  trackingLink?: string;
  imageAlt?: string;
  firebaseImageUrl?: string; // will be added after successful upload
  mainTermsText?: string; // Now stored per casino row as a table column
}

interface ICSVRow {
  ID: string;
  'Pretty Link': string;
  'Target URL': string;
  Clicks: string;
}

type Status = "pending" | "success" | "error";

/////////////////////////////////////////////
// Component
/////////////////////////////////////////////

const CasinoLinker: React.FC = () => {
  // Existing state
  const [baseUrl, setBaseUrl] = useState('');
  const [csvData, setCsvData] = useState<ICSVRow[]>([]);
  const [rawHtml, setRawHtml] = useState('');
  const [casinos, setCasinos] = useState<ICasino[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [shortcode, setShortcode] = useState('');
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showNewCategoryPopup, setShowNewCategoryPopup] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [casinoStatuses, setCasinoStatuses] = useState<{ [key: string]: Status }>({});
  const [parallelLimit, setParallelLimit] = useState<number>(5);
  const [dataReady, setDataReady] = useState(false);

  // New state for additional header and static texts
  const [headerTexts, setHeaderTexts] = useState({
    posText: '',
    casinoText: '',
    casinoBonusText: '',
    moreInfoText: '',
    moveOnText: ''
  });
  const [ctaButtonText, setCtaButtonText] = useState('');
  const [loadMoreButtonText, setLoadMoreButtonText] = useState('');

  // New state for GEO Slug and Page Slug
  const [geoSlug, setGeoSlug] = useState('');
  const [pageSlug, setPageSlug] = useState('');

  const { showNotification } = useNotification();
  const storage = getStorage();

  // When baseUrl and CSV are set, mark dataReady true
  useEffect(() => {
    if (baseUrl && csvData.length > 0) {
      setDataReady(true);
    }
  }, [baseUrl, csvData]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      console.log("Fetching categories...");
      const categoriesCollectionRef = collection(dbModernCasinos, 'categories');
      const querySnapshot = await getDocs(categoriesCollectionRef);
      const fetchedCategories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as { name: string })
      }));
      setCategories(fetchedCategories);
      console.log("Fetched categories:", fetchedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleShowNewCategoryPopup = () => setShowNewCategoryPopup(true);
  const handleHideNewCategoryPopup = () => {
    setShowNewCategoryPopup(false);
    setNewCategoryName('');
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      showNotification('Category name cannot be empty!', 'error');
      return;
    }
    try {
      const newCategoryRef = doc(dbModernCasinos, 'categories', newCategoryName.trim());
      const docSnapshot = await getDoc(newCategoryRef);
      if (docSnapshot.exists()) {
        setNewCategoryName('');
        showNotification('Category already exists!', 'error');
      } else {
        await setDoc(newCategoryRef, { name: newCategoryName.trim() });
        setCategories(prev => [...prev, { id: newCategoryName.trim(), name: newCategoryName.trim() }]);
        setNewCategoryName('');
        setShowNewCategoryPopup(false);
        showNotification(`Category ${newCategoryName.trim()} added successfully!`, 'success');
      }
    } catch (error: any) {
      showNotification('Failed to add category!', 'error');
    }
  };

  ////////////////////////////////////////////////////////////
  // CSV Upload
  ////////////////////////////////////////////////////////////
  const handleCSVUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (evt) => {
      if (typeof evt.target?.result === 'string') {
        const text = evt.target.result;
        const parsed = parseCSV(text);
        setCsvData(parsed);
        console.log("CSV parsed:", parsed);
        alert(`Parsed CSV with ${parsed.length} rows!`);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string): ICSVRow[] => {
    text = text.replace(/^\uFEFF/, '');
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"+|"+$/g, ''));
    const rows: ICSVRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const columns = lines[i].split(',');
      const rowObj: any = {};
      headers.forEach((header, index) => {
        rowObj[header] = columns[index] ? columns[index].trim() : '';
      });
      rows.push(rowObj as ICSVRow);
    }
    return rows;
  };

  ////////////////////////////////////////////////////////////
  // HTML Parse
  ////////////////////////////////////////////////////////////
  const handleScanAndAdd = () => {
    if (!rawHtml.trim()) {
      alert('Please paste some HTML to parse.');
      return;
    }
    console.log("Scanning HTML...");
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');
  
    // Parse header texts (only once)
    const headerRow = doc.querySelector(
      '.zms-sctable_ver3 .zms-sctablerow1, .slots-sctable_ver3 .slots-sctablerow1'
    );
    if (headerRow) {
      const posText =
        headerRow.querySelector(
          '.zms-zm-col.zms-rank-col strong, .slots-zm-col.slots-rank-col strong'
        )?.textContent?.trim() || '';
      const casinoText =
        headerRow.querySelector(
          '.zms-zm-col.zms-site-col strong, .slots-zm-col.slots-site-col strong'
        )?.textContent?.trim() || '';
      const casinoBonusText =
        headerRow.querySelector(
          '.zms-zm-col.zms-bonus-col strong, .slots-zm-col.slots-bonus-col strong'
        )?.textContent?.trim() || '';
      const moreInfoText =
        headerRow.querySelector(
          '.zms-zm-col.zms-rate-col strong, .slots-zm-col.slots-rate-col strong'
        )?.textContent?.trim() || '';
      const moveOnText =
        headerRow.querySelector(
          '.zms-zm-col.zms-vis-col strong, .slots-zm-col.slots-vis-col strong'
        )?.textContent?.trim() || '';
      setHeaderTexts({ posText, casinoText, casinoBonusText, moreInfoText, moveOnText });
    }
  
    // Parse casino rows
    const rowEls = doc.querySelectorAll(
      '.zms-sctablerow2, .main-column-text .zms-sctablerow2, .slots-sctablerow2, .main-column-text .slots-sctablerow2'
    );
    const extractedCasinos: ICasino[] = [];
    rowEls.forEach((row, index) => {
      // Check for the two different types of ribbon wrappers
      const ribbonWrapDefault = row.querySelector('.zms-wrap, .slots-wrap');
      const ribbonWrapNew = row.querySelector('.zms-wrap-new, .slots-wrap-new');
      const hasRibbon = !!(ribbonWrapDefault || ribbonWrapNew);
      const ribbonType = ribbonWrapNew ? 'new' : (ribbonWrapDefault ? 'default' : '');
      const ribbonSpan = row.querySelector(
        '.zms-ribbon span, .zms-ribbon-new span, .slots-ribbon span, .slots-ribbon-new span'
      );
      const ribbonText = ribbonSpan?.textContent?.trim() || '';
  
      const positionEl = row.querySelector('.zms_ratecir, .slots_ratecir');
      const positionText = positionEl?.textContent?.trim() || '0';
      const positionNumber = Number(positionText) || 0;
  
      const imgEl = row.querySelector(
        '.zms-site-col img, .zms-zm2-col.zms-site-col img, .slots-site-col img, .slots-tbx-col.slots-site-col img'
      );
      const imgSrcRaw = imgEl?.getAttribute('src') || '';
      // Remove duplicate base URL if present
      let imgSrc = imgSrcRaw;
      if (imgSrc.startsWith('http') && imgSrc.includes(baseUrl)) {
        imgSrc = imgSrc.replace(baseUrl, '');
      }
      if (!imgSrc.startsWith('/')) {
        imgSrc = '/' + imgSrc;
      }
      const imageUrl = baseUrl.replace(/\/$/, '') + imgSrc;
      const imageAlt = imgEl?.getAttribute('alt') || '';
  
      const bonusEl = row.querySelector(
        '.zms_bonper.zmsbonus_text, .slots_bonper.slotsbonus_text'
      );
      const bonus = bonusEl?.textContent?.trim() || '';
  
      const termsBonusEl = row.querySelector(
        '.zms-bonus-col p[style*="font-size:7px"], .slots-bonus-col p'
      );
      const termsBonusHTML = termsBonusEl?.innerHTML || '';
      const bonusDoc = new DOMParser().parseFromString(termsBonusHTML, 'text/html');
      const anchorEls = bonusDoc.querySelectorAll('a');
      const termsLinks = Array.from(anchorEls).map(a => a.outerHTML);
      anchorEls.forEach(a => a.remove());
      let termsPlain = bonusDoc.body.textContent?.trim() || '';
      termsPlain = termsPlain.replace(/(\|\s*\.)+\s*$/, '').replace(/\|\s*$/, '').trim();
  
      const bulletLis = row.querySelectorAll(
        '.zms-rate-col.list li, .slots-rate-col.list li'
      );
      const allTerms = Array.from(bulletLis).map(li => {
        const liText = li.textContent?.trim() || '';
        return liText.replace(/✓\s*/g, '').trim();
      });
      const [term1, term2, term3] = allTerms;
  
      const gotoLinkEl = row.querySelector('.zms-button, .slots-button');
      let rawGotoLink = gotoLinkEl?.getAttribute('href') || '';
      const gotoLink = removeBaseUrlPath(baseUrl, rawGotoLink);
  
      // For the first casino row, capture CTA button text
      if (index === 0) {
        const ctaButtonEl = row.querySelector(
          '.zms-zm2-col.zms-vis-col a.zms-button, .slots-tbx-col.slots-vis-col a.slots-button'
        );
        const ctaText = ctaButtonEl?.textContent?.trim() || '';
        setCtaButtonText(ctaText);
      }
  
      // Parse main terms text for each casino row
      const mainTermsEl = row.querySelector('.zms-desc p');
      const mainTerms = mainTermsEl?.textContent?.trim() || '';
  
      extractedCasinos.push({
        hasRibbon,
        ribbonType,
        ribbonText,
        position: positionNumber,
        image: imageUrl,
        bonus,
        termsPlain,
        termsLinks,
        term1,
        term2,
        term3,
        gotoLink,
        imageAlt,
        mainTermsText: mainTerms
      });
    });
  
    const updatedCasinos: ICasino[] = extractedCasinos.map(casino => {
      let matchFound: ICSVRow | undefined = undefined;
      for (const row of csvData) {
        if (casino.gotoLink === row['Pretty Link']) {
          matchFound = row;
          break;
        }
      }
      return matchFound ? { ...casino, trackingLink: matchFound['Target URL'] } : casino;
    });
    console.log("Updated casinos:", updatedCasinos);
    setCasinos(updatedCasinos);
  
    // Parse the load more button text (only once)
    const loadMoreEl = doc.querySelector('.btncontainer button.loadmorebtn');
    if (loadMoreEl) {
      const loadMoreText = loadMoreEl.textContent?.trim() || '';
      setLoadMoreButtonText(loadMoreText);
    }
  };
  

  function removeBaseUrlPath(baseUrl: string, gotoLink: string): string {
    try {
      const base = new URL(baseUrl);
      if (gotoLink.startsWith(base.href)) {
        gotoLink = gotoLink.replace(base.href, '');
      }
      if (gotoLink.startsWith(base.origin)) {
        gotoLink = gotoLink.replace(base.origin, '');
      }
      const cleanBasePath = base.pathname.replace(/\/+$/, '');
      if (cleanBasePath && gotoLink.startsWith(cleanBasePath)) {
        gotoLink = gotoLink.slice(cleanBasePath.length);
      }
      gotoLink = gotoLink.replace(/^\/+/, '').replace(/\/+$/, '');
      if (gotoLink.startsWith('ca/')) {
        gotoLink = gotoLink.slice(3);
      }
      return gotoLink;
    } catch {
      return gotoLink.replace(/^\/+/, '').replace(/\/+$/, '');
    }
  }

  ////////////////////////////////////////////////////////////
  // Save Casinos & Upload Images using Firebase Function with parallel processing
  ////////////////////////////////////////////////////////////
  const handleSaveCasinos = async () => {
    if (casinos.length === 0) {
      showNotification('No casinos to save!', 'error');
      return;
    }
    if (!shortcode.trim()) {
      showNotification('Shortcode Name is required!', 'error');
      return;
    }
    if (!selectedCategory) {
      showNotification('Please select a category!', 'error');
      return;
    }
    if (!geoSlug.trim()) {
      showNotification('GEO Slug is required!', 'error');
      return;
    }
    if (!pageSlug.trim()) {
      showNotification('Page Slug is required!', 'error');
      return;
    }
    console.log("Starting save process...");
    setIsSaving(true);
    setUploadProgress(0);
    let errorList: string[] = [];
    // Initialize statuses for each casino based on gotoLink
    const initialStatuses: { [key: string]: Status } = {};
    casinos.forEach((casino: ICasino) => {
      initialStatuses[casino.gotoLink] = "pending";
    });
    setCasinoStatuses(initialStatuses);

    try {
      console.log("Checking if parent document exists for shortcode:", shortcode);
      const shortcodeDocRef = doc(dbModernCasinos, 'casinosets', shortcode);
      const docSnapshot = await getDoc(shortcodeDocRef);
      if (docSnapshot.exists()) {
        showNotification('Shortcode Name already exists. Please choose another name.', 'error');
        setIsSaving(false);
        return;
      }
      console.log("Creating parent document...");
      await setDoc(shortcodeDocRef, { 
        createdAt: new Date(), 
        category: selectedCategory,
        headerTexts,
        ctaButtonText,
        loadMoreButtonText,
        geoSlug,    // Save GEO Slug
        pageSlug    // Save Page Slug
      });
      const functionEndpoint = 'https://us-central1-dream-lead.cloudfunctions.net/fetchAndStoreImage';
      console.log("Using Firebase Function endpoint:", functionEndpoint);

      // Chunk casinos array into groups based on parallelLimit
      const chunks: ICasino[][] = [];
      for (let i = 0; i < casinos.length; i += parallelLimit) {
        chunks.push(casinos.slice(i, i + parallelLimit));
      }

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        console.log(`Processing chunk ${i + 1}/${chunks.length} with ${chunk.length} casinos in parallel`);
        await Promise.all(chunk.map(async (casino: ICasino) => {
          try {
            console.log("Processing casino:", casino.gotoLink);
            const functionUrl = `${functionEndpoint}?url=${encodeURIComponent(casino.image)}`;
            console.log("Calling function URL:", functionUrl);
            const response = await fetch(functionUrl);
            console.log("Response status for casino", casino.gotoLink, ":", response.status);
            if (!response.ok) {
              throw new Error(`Function error: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            console.log("Function returned data for casino", casino.gotoLink, ":", data);
            const firebaseImageUrl = data.publicUrl;
            if (!firebaseImageUrl) {
              throw new Error('No publicUrl returned from function');
            }
            console.log("Storing casino document with image URL:", firebaseImageUrl);
            await addDoc(
              collection(dbModernCasinos, 'casinosets', shortcode, 'casinos'),
              { ...casino, firebaseImageUrl }
            );
            setCasinos(prev =>
              prev.map((item: ICasino) =>
                item.gotoLink === casino.gotoLink ? { ...item, firebaseImageUrl } : item
              )
            );
            setCasinoStatuses(prev => ({ ...prev, [casino.gotoLink]: "success" }));
            console.log("Successfully processed casino:", casino.gotoLink);
          } catch (error: any) {
            console.error("Error saving casino with gotoLink", casino.gotoLink, error);
            errorList.push(`Casino "${casino.gotoLink}": ${error.message}`);
            setCasinoStatuses(prev => ({ ...prev, [casino.gotoLink]: "error" }));
            showNotification(`Error saving casino "${casino.gotoLink}": ${error.message}`, 'error');
          }
        }));
        // Update overall progress after each chunk
        setUploadProgress(prev => prev + chunk.length);
      }
      setSaveErrors(errorList);
      if (errorList.length > 0) {
        showNotification('Some casinos were not saved successfully.', 'error');
      } else {
        showNotification('Casinos saved successfully!', 'success');
      }
    } catch (error: any) {
      console.error("Error during saving casinos:", error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
      console.log("Save process completed. Upload progress:", uploadProgress, "Errors:", errorList);
    }
  };

  ////////////////////////////////////////////////////////////
  // Render
  ////////////////////////////////////////////////////////////
  return (
    <div className="dashpanel">
      <div className="main">
        <div className="head" style={{maxWidth:'1800px'}}>
          <h2>Casino Linker</h2>
          <div style={{ marginBottom: '1rem', marginTop:'1rem' }}>
            <Link 
              to="/console/shortcodes"
              style={{
                backgroundColor: '#007BFF',
                color: '#fff',
                fontWeight: 'bold',
                padding: '10px 15px',
                borderRadius: '5px',
                textDecoration: 'none'
              }}
            >
              Back to Shortcodes
            </Link>
          </div>
          {/* Step 1: Base URL and CSV Upload */}
          <div style={{ marginBottom: '1rem' }}>
            <label>
              <strong>Base URL:</strong><br />
              <input
                type="text"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://example.com"
                style={{ width: '100%', marginTop: '0.25rem' }}
              />
            </label>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', backgroundColor: '#007BFF', color: '#fff', fontWeight: 'bold', padding: '10px', borderRadius: '5px', cursor: 'pointer' }}>
              Upload CSV:
              <input type="file" accept=".csv" onChange={handleCSVUpload} style={{ display: 'none' }} />
            </label>
          </div>
          {/* Step 2: Once Base URL and CSV are ready, show HTML input with slider and styled SVG button */}
          {dataReady && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  <strong>Process {parallelLimit} casinos at once:</strong><br />
                  <input 
                    type="range" 
                    min="1" 
                    max="20" 
                    value={parallelLimit} 
                    onChange={(e) => setParallelLimit(Number(e.target.value))}
                    style={{ width: '100%' }}
                  />
                </label>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  <strong>Paste HTML to Parse:</strong><br />
                  <textarea
                    value={rawHtml}
                    onChange={(e) => setRawHtml(e.target.value)}
                    rows={10}
                    style={{ width: '100%', marginTop: '0.25rem' }}
                    placeholder="Paste your .zms-sctablerow2 HTML snippet here..."
                  />
                </label>
              </div>
              <button className="svg-button" onClick={handleScanAndAdd} style={{
                backgroundColor: '#007BFF',
                color: '#fff',
                fontWeight: 'bold',
                padding: '10px 15px',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M12 2L15 8L22 9L17 14L18 21L12 18L6 21L7 14L2 9L9 8L12 2Z" fill="#fff"/>
                </svg>
                Scan HTML
              </button>
            </>
          )}
          {/* Step 4: Shortcode Name, Category Selector and Additional Header Fields */}
          {casinos.length > 0 && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  <strong>Shortcode Name:</strong><br />
                  <input
                    type="text"
                    value={shortcode}
                    onChange={(e) => setShortcode(e.target.value)}
                    placeholder="Enter shortcode name"
                    style={{ width: '100%', marginTop: '0.25rem' }}
                  />
                </label>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  <strong>Category:</strong><br />
                  <div className="flatcat">
                    <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
                      style={{ width: '200px', padding: '10px', borderRadius: '5px', border: '1px solid #ccc', fontWeight: 'bold', color: '#007BFF' }}>
                      <option value="">Select a category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    <div className="plus" onClick={handleShowNewCategoryPopup} style={{
                      backgroundColor: '#007BFF',
                      color: '#fff',
                      fontWeight: 'bold',
                      padding: '5px 10px',
                      borderRadius: '50%',
                      marginLeft: '5px',
                      cursor: 'pointer'
                    }}>+</div>
                  </div>
                </label>
              </div>
              {/* New GEO Slug Input */}
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  <strong>GEO Slug:</strong><br />
                  <input
                    type="text"
                    value={geoSlug}
                    onChange={(e) => setGeoSlug(e.target.value)}
                    placeholder="de"
                    style={{ width: '100%', marginTop: '0.25rem' }}
                  />
                </label>
              </div>
              {/* New Page Slug Input */}
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  <strong>Page Slug:</strong><br />
                  <input
                    type="text"
                    value={pageSlug}
                    onChange={(e) => setPageSlug(e.target.value)}
                    placeholder="free"
                    style={{ width: '100%', marginTop: '0.25rem' }}
                  />
                </label>
              </div>
              {/* Header Texts Inputs (in one row) */}
              {headerTexts.posText && (
                <div className="header-texts-inputs" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <input
                    type="text"
                    value={headerTexts.posText}
                    onChange={(e) => setHeaderTexts({ ...headerTexts, posText: e.target.value })}
                    placeholder="POS"
                  />
                  <input
                    type="text"
                    value={headerTexts.casinoText}
                    onChange={(e) => setHeaderTexts({ ...headerTexts, casinoText: e.target.value })}
                    placeholder="CASINO"
                  />
                  <input
                    type="text"
                    value={headerTexts.casinoBonusText}
                    onChange={(e) => setHeaderTexts({ ...headerTexts, casinoBonusText: e.target.value })}
                    placeholder="CASINO BONUS"
                  />
                  <input
                    type="text"
                    value={headerTexts.moreInfoText}
                    onChange={(e) => setHeaderTexts({ ...headerTexts, moreInfoText: e.target.value })}
                    placeholder="MORE INFO"
                  />
                  <input
                    type="text"
                    value={headerTexts.moveOnText}
                    onChange={(e) => setHeaderTexts({ ...headerTexts, moveOnText: e.target.value })}
                    placeholder="MOVE ON"
                  />
                </div>
              )}
              {/* CTA Button Text Input */}
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  <strong>CTA Button Text:</strong><br />
                  <input
                    type="text"
                    value={ctaButtonText}
                    onChange={(e) => setCtaButtonText(e.target.value)}
                    placeholder="CLAIM BONUS"
                    style={{ width: '100%' }}
                  />
                </label>
              </div>
              {/* Load More Button Text Input */}
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  <strong>Load More Button Text:</strong><br />
                  <input
                    type="text"
                    value={loadMoreButtonText}
                    onChange={(e) => setLoadMoreButtonText(e.target.value)}
                    placeholder="Load more casinos..."
                    style={{ width: '100%' }}
                  />
                </label>
              </div>
            </>
          )}
          {/* Step 5: Save Casinos Button */}
          {casinos.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <button onClick={handleSaveCasinos} disabled={isSaving} className="button-primary" style={{
                backgroundColor: '#007BFF',
                color: '#fff',
                fontWeight: 'bold',
                padding: '10px 15px',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}>
                {isSaving ? 'Processing Casinos...' : 'Save Casinos'}
              </button>
            </div>
          )}
          {isSaving && (
            <div style={{ marginBottom: '1rem' }}>
              <p>Processing {uploadProgress}/{casinos.length} casinos...</p>
              <progress value={uploadProgress} max={casinos.length} style={{ width: '100%' }} />
              {/* Display individual casino statuses */}
              <div style={{ marginTop: '1rem' }}>
                {casinos.map((casino: ICasino) => (
                  <div
                    key={casino.gotoLink}
                    style={{
                      padding: "5px",
                      marginBottom: "3px",
                      backgroundColor:
                        casinoStatuses[casino.gotoLink] === "success"
                          ? "#d4edda"
                          : casinoStatuses[casino.gotoLink] === "error"
                          ? "#f8d7da"
                          : "#fff3cd"
                    }}
                  >
                    Casino "{casino.gotoLink}": {casinoStatuses[casino.gotoLink] || "pending"}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Step 6: Display errors if any */}
          {saveErrors.length > 0 && (
            <div style={{ marginBottom: '1rem', color: 'red' }}>
              <h4>Errors occurred while saving:</h4>
              <ul>
                {saveErrors.map((err, index) => <li key={index}>{err}</li>)}
              </ul>
            </div>
          )}
          {/* Step 7: Results Table */}
          {casinos.length > 0 && (
            <>
              <h3 style={{ marginTop: '2rem' }}>Parsed Casinos</h3>
              <div style={{ width: '100%', overflowX: 'auto', marginTop: '1rem' }}>
                <table className="casino-table">
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th>Ribbon?</th>
                      <th>Ribbon Text</th>
                      <th>Ribbon Type</th>
                      <th>Position</th>
                      <th>Image</th>
                      <th>Alt Text</th>
                      <th>Bonus</th>
                      <th>Term 1</th>
                      <th>Term 2</th>
                      <th>Term 3</th>
                      <th>Terms (Plain)</th>
                      <th>Terms Link(s)</th>
                      <th>Main Terms Text</th>
                      <th>Goto Link</th>
                      <th>Tracking Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {casinos.map((c: ICasino, idx: number) => (
                      <tr key={idx} style={{ backgroundColor: casinoStatuses[c.gotoLink] === "success" ? "#d4edda" : undefined }}>
                        <td>{c.hasRibbon ? 'Yes' : 'No'}</td>
                        <td>{c.ribbonText}</td>
                        <td>{c.ribbonType}</td>
                        <td>{c.position}</td>
                        <td>
                          <img
                            src={c.firebaseImageUrl || c.image}
                            alt={c.imageAlt || 'Casino'}
                            style={{ maxWidth: '100px', borderRadius: '4px' }}
                          />
                        </td>
                        <td>{c.imageAlt}</td>
                        <td>{c.bonus}</td>
                        <td>{c.term1}</td>
                        <td>{c.term2}</td>
                        <td>{c.term3}</td>
                        <td>{c.termsPlain}</td>
                        <td>
                          {c.termsLinks.length > 0
                            ? c.termsLinks.map((linkHTML, i) => <div key={i} dangerouslySetInnerHTML={{ __html: linkHTML }} />)
                            : 'No links'}
                        </td>
                        <td>{c.mainTermsText}</td>
                        <td>{c.gotoLink}</td>
                        <td>{c.trackingLink || 'No match'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {/* New Category Popup */}
          {showNewCategoryPopup && (
            <div className="edit-overlay">
              <div className="delete-confirmation-popup">
                <div className="add-client-form edit-hreflang">
                  <h3>Add New Category</h3>
                  <label>Category Name</label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New Category Name"
                  />
                  <div className="buttonsetcontainer">
                    <button onClick={handleAddCategory} style={{
                      backgroundColor: '#007BFF',
                      color: '#fff',
                      fontWeight: 'bold',
                      padding: '10px 15px',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}>Add Category</button>
                    <button onClick={handleHideNewCategoryPopup} style={{
                      backgroundColor: '#007BFF',
                      color: '#fff',
                      fontWeight: 'bold',
                      padding: '10px 15px',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      marginLeft: '10px'
                    }}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CasinoLinker;
